package com.qrkontaq.ui.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.qrkontaq.data.dto.AdminMenuCategoryBlockDto
import com.qrkontaq.data.dto.AdminMenuDto
import com.qrkontaq.data.dto.AdminMenuItemDto
import com.qrkontaq.data.dto.CreateItemRequestDto
import com.qrkontaq.data.dto.OrderEntryDto
import com.qrkontaq.data.dto.UpdateCategoryRequestDto
import com.qrkontaq.data.dto.UpdateItemRequestDto
import com.qrkontaq.data.dto.currencySymbol
import com.qrkontaq.data.dto.localized
import com.qrkontaq.data.repository.AdminRepository
import com.qrkontaq.ui.components.forms.TextInput
import com.qrkontaq.ui.theme.Brand
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.qrkontaq.ui.tenant.InlineIcon
import com.qrkontaq.ui.tenant.MenuIcons
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.I
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLInputElement

private fun tagOptions() = listOf("vegan" to AdminT.tagVegan, "spicy" to AdminT.tagSpicy, "new" to AdminT.tagNew)

/** Accent colour + localized label for a dish tag, or null for unknown tags. */
private fun tagMeta(tag: String): Pair<String, String>? =
    when (tag) {
        "vegan" -> "#2E9E5B" to AdminT.tagVegan
        "spicy" -> "#E0654C" to AdminT.tagSpicy
        "new" -> "#E8A33D" to AdminT.tagNew
        else -> null
    }
/** Enabled site languages in a stable display order. */
private fun orderedLangs(languages: List<String>): List<String> {
    val preferred = SITE_LANGUAGE_OPTIONS.map { it.first }
    val result = preferred.filter { it in languages } + languages.filter { it !in preferred }
    return result.distinct().ifEmpty { listOf("en") }
}

private fun allergenOptions() =
    listOf(
        "gluten" to AdminT.allergenGluten, "lactose" to AdminT.allergenLactose, "nuts" to AdminT.allergenNuts,
        "eggs" to AdminT.allergenEggs, "fish" to AdminT.allergenFish, "soy" to AdminT.allergenSoy,
    )

@Composable
fun MenuTab(
    repository: AdminRepository,
    scope: CoroutineScope,
    rid: String,
) {
    InjectMenuStyles()

    var menu by remember(rid) { mutableStateOf<AdminMenuDto?>(null) }
    var languages by remember(rid) { mutableStateOf(listOf("uk")) }
    var currency by remember(rid) { mutableStateOf("EUR") }

    fun reload() {
        scope.launch { menu = runCatching { repository.getMenu(rid) }.getOrNull() }
    }
    LaunchedEffect(rid) {
        reload()
        val restaurant = runCatching { repository.getRestaurant(rid) }.getOrNull()
        languages = restaurant?.languages?.takeIf { it.isNotEmpty() } ?: listOf("en")
        restaurant?.currency?.takeIf { it.isNotBlank() }?.let { currency = it }
    }

    var stopListView by remember(rid) { mutableStateOf(false) }
    // Creation / editing happens in popups. null = closed; the payload carries the
    // record being edited (or null for a fresh one) plus the target category for dishes.
    var catModal by remember(rid) { mutableStateOf<CatModalState?>(null) }
    var dishModal by remember(rid) { mutableStateOf<DishModalState?>(null) }

    if (menu == null) {
        repeat(3) {
            Box(
                Modifier.fillMaxWidth().height(150.px).borderRadius(16.px).margin(bottom = 14.px)
                    .attrsModifier { classes("qk-skeleton") },
            )
        }
        return
    }

    val categories = menu?.categories.orEmpty()
    val dishCount = categories.sumOf { it.items.size }
    val featuredCount = categories.sumOf { block -> block.items.count { it.isFeatured } }

    Div({ classes("qk-menu", "qk-fade-in") }) {
        // ---- hero: page identity + live stats ----
        Div({ classes("qk-menu-hero") }) {
            Div({ classes("qk-menu-hero-head") }) {
                Div({ classes("ic") }) { InlineIcon(MenuIcons.Utensils, 26, "#FFFFFF") }
                Div {
                    H1({ classes("qk-menu-disp") }) { Text(AdminT.tabMenu) }
                    P { Text(AdminT.tabMenuSub) }
                }
            }
            Div({ classes("qk-menu-stats") }) {
                MenuStat(categories.size, AdminT.menuStatCategories)
                MenuStat(dishCount, AdminT.menuStatDishes)
                MenuStat(featuredCount, AdminT.menuStatFeatured)
            }
        }

        // ---- menu / stop-list segmented switch ----
        Div({ classes("qk-menu-seg") }) {
            SegTab(MenuIcons.Utensils, AdminT.tabMenu, on = !stopListView) { stopListView = false }
            SegTab(AdminIcons.Ban, AdminT.tabStopList, on = stopListView) { stopListView = true }
        }

        if (stopListView) {
            StopListTab(repository, scope, rid)
        } else {
        // ---- top action buttons: open the create popups ----
        Div({ classes("qk-menu-actions") }) {
            AddButton(AdminT.addCategory, primary = true) { catModal = CatModalState(null) }
            // A dish can be created without a category; the popup offers the existing ones.
            AddButton(AdminT.addDish, primary = true) {
                dishModal = DishModalState(existing = null, categoryId = null)
            }
        }

        if (categories.isEmpty()) {
            Div({ classes("qk-menu-empty") }) {
                Div({ classes("ic") }) { InlineIcon(MenuIcons.Utensils, 28, "#0B7A55") }
                org.jetbrains.compose.web.dom.H3 { Text(AdminT.menuEmptyTitle) }
                P { Text(AdminT.menuEmptyHint) }
            }
        }

        categories.forEachIndexed { index, block ->
            CategoryEditor(
                repository = repository,
                scope = scope,
                rid = rid,
                languages = languages,
                currency = currency,
                index = index,
                block = block,
                canMoveUp = index > 0,
                canMoveDown = index < categories.size - 1,
                onMove = { up ->
                val reordered = categories.toMutableList()
                val target = if (up) index - 1 else index + 1
                val tmp = reordered[index]
                reordered[index] = reordered[target]
                reordered[target] = tmp
                scope.launch {
                    runCatching {
                        repository.reorderCategories(
                            rid,
                            reordered.mapIndexed { i, c -> OrderEntryDto(c.category.id, i) },
                        )
                    }
                    reload()
                }
            },
            onEditCategory = { catModal = CatModalState(block) },
            onAddDish = { dishModal = DishModalState(existing = null, categoryId = block.category.id) },
            onEditDish = { item -> dishModal = DishModalState(existing = item, categoryId = item.categoryId) },
            onChanged = { reload() },
        )
        }

        // ---- popups ----
        catModal?.let { state ->
            CategoryModal(
                repository = repository,
                scope = scope,
                rid = rid,
                languages = languages,
                existing = state.existing,
                onClose = { catModal = null },
                onSaved = { catModal = null; reload() },
            )
        }
        dishModal?.let { state ->
            DishModal(
                repository = repository,
                scope = scope,
                rid = rid,
                languages = languages,
                currency = currency,
                categories = categories,
                existing = state.existing,
                initialCategoryId = state.categoryId,
                onClose = { dishModal = null },
                onSaved = { dishModal = null; reload() },
            )
        }
        }
    }
}

/** State for the category create/edit popup — [existing] null means "create". */
private data class CatModalState(val existing: AdminMenuCategoryBlockDto?)

/** State for the dish create/edit popup — [existing] null means "create". */
private data class DishModalState(val existing: AdminMenuItemDto?, val categoryId: String?)

/** Prominent pill button that opens a create popup. */
@Composable
private fun AddButton(label: String, primary: Boolean, onClick: () -> Unit) {
    org.jetbrains.compose.web.dom.Button({
        classes("qk-menu-add", if (primary) "pri" else "sec")
        onClick { onClick() }
    }) {
        Span({ classes("p") }) { InlineIcon(AdminIcons.Plus, 14, "currentColor") }
        Span { Text(label) }
    }
}

/** One glass stat block inside the menu hero. */
@Composable
private fun MenuStat(value: Int, label: String) {
    Div({ classes("s") }) {
        B { Text(value.toString()) }
        Span { Text(label) }
    }
}

/** A pill in the menu / stop-list segmented switch. */
@Composable
private fun SegTab(iconSvg: String, label: String, on: Boolean, onClick: () -> Unit) {
    Div({
        classes("t")
        if (on) classes("on")
        onClick { onClick() }
    }) {
        InlineIcon(iconSvg, 15, "currentColor")
        Span { Text(label) }
    }
}

@Composable
private fun CategoryEditor(
    repository: AdminRepository,
    scope: CoroutineScope,
    rid: String,
    languages: List<String>,
    currency: String,
    index: Int,
    block: AdminMenuCategoryBlockDto,
    canMoveUp: Boolean,
    canMoveDown: Boolean,
    onMove: (up: Boolean) -> Unit,
    onEditCategory: () -> Unit,
    onAddDish: () -> Unit,
    onEditDish: (AdminMenuItemDto) -> Unit,
    onChanged: () -> Unit,
) {
    val hidden = !block.category.isVisible

    Div({
        classes("qk-cat", "qk-rise")
        if (hidden) classes("off")
    }) {
        Div({ classes("qk-cat-accent") })
        Div({
            style {
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "space-between")
                property("gap", "12px")
            }
        }) {
            // left: index chip + name + dish count
            Div({
                style {
                    property("display", "flex")
                    property("align-items", "center")
                    property("gap", "12px")
                    property("min-width", "0")
                }
            }) {
                Div({ classes("qk-cat-idx") }) { Text("${index + 1}") }
                Div({ style { property("min-width", "0") } }) {
                    Div({
                        style {
                            property("display", "flex")
                            property("align-items", "center")
                            property("gap", "8px")
                            property("flex-wrap", "wrap")
                        }
                    }) {
                        Span({ classes("qk-cat-name") }) { Text(block.category.name.localized("uk")) }
                        if (block.category.isBar) AdminBadge(AdminT.barBadge, "neutral")
                        if (hidden) AdminBadge(AdminT.hiddenBadge, "warning")
                    }
                    Div({ style { property("margin-top", "4px") } }) {
                        Span({ classes("qk-cat-count") }) {
                            Text("${block.items.size} ${AdminT.menuStatDishes}")
                        }
                    }
                }
            }
            // right: reorder + visibility + delete
            val setVisible: (Boolean) -> Unit = { visible ->
                scope.launch {
                    runCatching {
                        repository.updateCategory(
                            rid,
                            block.category.id,
                            UpdateCategoryRequestDto(isVisible = visible),
                        )
                    }
                    onChanged()
                }
            }
            val deleteCategory: () -> Unit = {
                if (kotlinx.browser.window.confirm(
                        AdminT.deleteCategoryConfirm(block.category.name.localized("uk")),
                    )
                ) {
                    scope.launch {
                        runCatching { repository.deleteCategory(rid, block.category.id, force = true) }
                        onChanged()
                    }
                }
            }
            // Desktop: the familiar inline icon strip.
            Div({ classes("qk-acts-full") }) {
                if (canMoveUp) IconAction(AdminIcons.ChevronUp, AdminT.moveUp, onClick = { onMove(true) })
                if (canMoveDown) IconAction(AdminIcons.ChevronDown, AdminT.moveDown, onClick = { onMove(false) })
                IconAction(AdminIcons.Pencil, AdminT.editCategory, onClick = { onEditCategory() })
                Box(Modifier.margin(leftRight = 8.px)) {
                    AdminToggle(checked = block.category.isVisible, onToggle = setVisible)
                }
                IconAction(AdminIcons.Trash, AdminT.deleteCategory, danger = true, onClick = deleteCategory)
            }
            // Phones: a single "⋮" button — long category names get the row back.
            Div({ classes("qk-acts-more") }) {
                MoreActionsMenu(
                    buildList {
                        if (canMoveUp) add(MoreAction(AdminIcons.ChevronUp, AdminT.moveUp) { onMove(true) })
                        if (canMoveDown) add(MoreAction(AdminIcons.ChevronDown, AdminT.moveDown) { onMove(false) })
                        add(MoreAction(AdminIcons.Pencil, AdminT.editCategory) { onEditCategory() })
                        add(
                            if (block.category.isVisible) {
                                MoreAction(AdminIcons.EyeOff, AdminT.hideLabel) { setVisible(false) }
                            } else {
                                MoreAction(AdminIcons.Eye, AdminT.showLabel) { setVisible(true) }
                            },
                        )
                        add(MoreAction(AdminIcons.Trash, AdminT.deleteCategory, danger = true, onClick = deleteCategory))
                    },
                )
            }
        }

        block.items.forEachIndexed { itemIndex, item ->
            ItemRow(
                repository = repository,
                scope = scope,
                rid = rid,
                languages = languages,
                currency = currency,
                item = item,
                canMoveUp = itemIndex > 0,
                canMoveDown = itemIndex < block.items.size - 1,
                onMoveItem = { up ->
                    val reordered = block.items.toMutableList()
                    val target = if (up) itemIndex - 1 else itemIndex + 1
                    val tmp = reordered[itemIndex]
                    reordered[itemIndex] = reordered[target]
                    reordered[target] = tmp
                    scope.launch {
                        runCatching {
                            repository.reorderItems(
                                rid,
                                reordered.mapIndexed { i, it -> OrderEntryDto(it.id, i) },
                            )
                        }
                        onChanged()
                    }
                },
                onEdit = { onEditDish(item) },
                onChanged = onChanged,
            )
        }

        AdminButton(
            text = AdminT.addDishPlus,
            secondary = true,
            modifier = Modifier.margin(top = 8.px),
            onClick = { onAddDish() },
        )
    }
}

@Composable
private fun ItemRow(
    repository: AdminRepository,
    scope: CoroutineScope,
    rid: String,
    languages: List<String>,
    currency: String,
    item: AdminMenuItemDto,
    canMoveUp: Boolean,
    canMoveDown: Boolean,
    onMoveItem: (up: Boolean) -> Unit,
    onEdit: () -> Unit,
    onChanged: () -> Unit,
) {
    val toast = com.qrkontaq.ui.components.notifications.LocalToastState.current
    val off = !item.isAvailable

    Div({
        classes("qk-item")
        if (off) classes("off")
    }) {
        // ---- left: thumb + name + meta ----
        Div({
            style {
                property("display", "flex")
                property("align-items", "center")
                property("gap", "12px")
                property("min-width", "0")
            }
        }) {
            val photo = item.photoUrl
            if (photo != null) {
                Img(src = photo, attrs = { classes("qk-thumb") })
            } else {
                Div({ classes("qk-thumb-ph") }) { InlineIcon(MenuIcons.Utensils, 20, "#C9C3BA") }
            }
            Div({ style { property("min-width", "0") } }) {
                Div({
                    style {
                        property("display", "flex")
                        property("align-items", "center")
                        property("gap", "6px")
                        property("flex-wrap", "wrap")
                    }
                }) {
                    Span({ classes("qk-item-name") }) { Text(item.name.localized("uk")) }
                    if (item.isFeatured) InlineIcon(MenuIcons.StarFilled, 13, "#E8A33D")
                    if (off) AdminBadge(AdminT.stopBadge, "danger")
                }
                val tags = item.tags.mapNotNull { tagMeta(it) }
                val weight = item.weight?.takeIf { it.isNotBlank() }
                if (tags.isNotEmpty() || weight != null) {
                    Div({
                        style {
                            property("display", "flex")
                            property("align-items", "center")
                            property("gap", "12px")
                            property("flex-wrap", "wrap")
                            property("margin-top", "5px")
                        }
                    }) {
                        tags.forEach { (color, label) ->
                            Div({ classes("qk-tagdot") }) {
                                I({ style { property("background-color", color) } })
                                Text(label)
                            }
                        }
                        weight?.let {
                            Span({
                                style {
                                    property("font-size", "11px")
                                    property("color", "#9CA3AF")
                                }
                            }) { Text(it) }
                        }
                    }
                }
            }
        }
        // ---- right: price pill + actions ----
        val duplicate: () -> Unit = {
            scope.launch {
                val copyName = item.name.toMutableMap()
                val primary =
                    orderedLangs(languages).firstOrNull { !copyName[it].isNullOrBlank() }
                        ?: copyName.keys.firstOrNull() ?: "en"
                copyName[primary] = (copyName[primary] ?: copyName.values.firstOrNull() ?: "") + AdminT.copySuffix
                val created =
                    runCatching {
                        repository.createItem(
                            rid,
                            CreateItemRequestDto(
                                categoryId = item.categoryId,
                                name = copyName,
                                description = item.description,
                                priceMinor = item.priceMinor,
                                photoUrl = item.photoUrl,
                                weight = item.weight,
                                tags = item.tags,
                                allergens = item.allergens,
                                calories = item.calories,
                                isFeatured = false,
                            ),
                        ).data
                    }.getOrNull()
                if (created != null) toast.success(AdminT.dishDuplicated) else toast.error(AdminT.duplicateFailed)
                onChanged()
            }
        }
        val deleteDish: () -> Unit = {
            if (kotlinx.browser.window.confirm(AdminT.deleteDishConfirm(item.name.localized("uk")))) {
                scope.launch {
                    val ok = runCatching { repository.deleteItem(rid, item.id) }.isSuccess
                    if (ok) toast.success(AdminT.dishDeleted) else toast.error(AdminT.deleteFailed)
                    onChanged()
                }
            }
        }
        Div({
            style {
                property("display", "flex")
                property("align-items", "center")
                property("flex", "none")
            }
        }) {
            Div({ classes("qk-price") }) {
                Text("${item.priceMinor / 100}")
                Span { Text(" ${currencySymbol(currency)}") }
            }
            // Desktop: the familiar inline icon strip.
            Div({ classes("qk-acts-full") }) {
                if (canMoveUp) IconAction(AdminIcons.ChevronUp, AdminT.moveUp, onClick = { onMoveItem(true) })
                if (canMoveDown) IconAction(AdminIcons.ChevronDown, AdminT.moveDown, onClick = { onMoveItem(false) })
                IconAction(AdminIcons.Copy, AdminT.duplicate, onClick = duplicate)
                IconAction(AdminIcons.Pencil, AdminT.edit, onClick = { onEdit() })
                IconAction(AdminIcons.Trash, AdminT.delete, danger = true, onClick = deleteDish)
            }
            // Phones: everything behind one "⋮" so long dish names don't collide.
            Div({ classes("qk-acts-more") }) {
                MoreActionsMenu(
                    buildList {
                        if (canMoveUp) add(MoreAction(AdminIcons.ChevronUp, AdminT.moveUp) { onMoveItem(true) })
                        if (canMoveDown) add(MoreAction(AdminIcons.ChevronDown, AdminT.moveDown) { onMoveItem(false) })
                        add(MoreAction(AdminIcons.Copy, AdminT.duplicate, onClick = duplicate))
                        add(MoreAction(AdminIcons.Pencil, AdminT.edit) { onEdit() })
                        add(MoreAction(AdminIcons.Trash, AdminT.delete, danger = true, onClick = deleteDish))
                    },
                )
            }
        }
    }
}

@Composable
private fun ItemForm(
    repository: AdminRepository,
    scope: CoroutineScope,
    rid: String,
    languages: List<String>,
    currency: String,
    categories: List<AdminMenuCategoryBlockDto>,
    initialCategoryId: String?,
    existing: AdminMenuItemDto?,
    onDone: () -> Unit,
) {
    // null = the dish belongs to no category (allowed); a value pins it to one.
    var categoryId by remember { mutableStateOf(initialCategoryId) }
    var names by remember { mutableStateOf(existing?.name.orEmpty()) }
    var descriptions by remember { mutableStateOf(existing?.description.orEmpty()) }
    var priceUah by remember {
        mutableStateOf(existing?.let { (it.priceMinor / 100.0).toString().removeSuffix(".0") }.orEmpty())
    }
    var weight by remember { mutableStateOf(existing?.weight.orEmpty()) }
    var calories by remember { mutableStateOf(existing?.calories?.toString().orEmpty()) }
    var tags by remember { mutableStateOf(existing?.tags.orEmpty().toSet()) }
    var allergens by remember { mutableStateOf(existing?.allergens.orEmpty().toSet()) }
    var featured by remember { mutableStateOf(existing?.isFeatured ?: false) }
    var photoUrl by remember { mutableStateOf(existing?.photoUrl) }
    var uploading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    val toast = com.qrkontaq.ui.components.notifications.LocalToastState.current

    Column(Modifier.fillMaxWidth()) {
        // Which category this dish belongs to — "no category" is allowed, and an
        // edit can move it elsewhere. Chooser hidden only when there is nothing to pick.
        if (categories.isNotEmpty()) {
            AdminHint(AdminT.categoryLabel)
            Div({ classes("qk-menu-catpick") }) {
                Div({
                    classes("qk-menu-catchip")
                    if (categoryId == null) classes("on")
                    onClick { categoryId = null }
                }) { Text(AdminT.noCategory) }
                categories.forEach { c ->
                    val sel = c.category.id == categoryId
                    Div({
                        classes("qk-menu-catchip")
                        if (sel) classes("on")
                        onClick { categoryId = c.category.id }
                    }) { Text(c.category.name.localized("uk")) }
                }
            }
        }
        // Hidden languages keep their translations: state holds the full map and the
        // save payload includes it, so re-enabling a language brings the content back.
        val formLangs = orderedLangs(languages)
        Row(Modifier.fillMaxWidth().flexWrap(org.jetbrains.compose.web.css.FlexWrap.Wrap).gap(6.px)) {
            formLangs.forEach { code ->
                Box(Modifier.styleModifier { property("flex", "1"); property("min-width", "170px") }) {
                    TextInput(
                        value = names[code].orEmpty(),
                        onValueChange = { names = names + (code to it) },
                        label = "${AdminT.nameLabel} (${code.uppercase()})",
                    )
                }
            }
        }
        formLangs.forEach { code ->
            TextInput(
                value = descriptions[code].orEmpty(),
                onValueChange = { descriptions = descriptions + (code to it) },
                label = "${AdminT.descriptionLabel} (${code.uppercase()})",
                placeholder = if (code == "uk") AdminT.descriptionPh else "",
            )
        }
        Row(Modifier.fillMaxWidth()) {
            Box(Modifier.fillMaxWidth().margin(right = 6.px)) {
                TextInput(value = priceUah, onValueChange = { priceUah = it }, label = "${AdminT.priceLabel} ${currencySymbol(currency)}")
            }
            Box(Modifier.fillMaxWidth().margin(right = 6.px)) {
                TextInput(value = weight, onValueChange = { weight = it }, label = AdminT.weightLabel, placeholder = AdminT.weightPh)
            }
            Box(Modifier.fillMaxWidth()) {
                TextInput(value = calories, onValueChange = { calories = it }, label = AdminT.kcalLabel)
            }
        }

        ChipPicker(AdminT.tagsLabel, tagOptions(), tags) { tags = it }
        ChipPicker(AdminT.allergensLabel, allergenOptions(), allergens) { allergens = it }

        Row(Modifier.margin(topBottom = 8.px), verticalAlignment = Alignment.CenterVertically) {
            AdminToggle(checked = featured) { featured = it }
            SpanText(
                AdminT.chefRecommends,
                Modifier.fontSize(13.px).color(Brand.Charcoal).margin(left = 8.px),
            )
        }

        Row(verticalAlignment = Alignment.CenterVertically) {
            photoUrl?.let { photo ->
                Image(
                    src = photo,
                    modifier = Modifier.size(56.px).minWidth(56.px).borderRadius(12.px)
                        .objectFit(ObjectFit.Cover).margin(right = 10.px),
                )
            }
            UploadButton(
                label = if (uploading) AdminT.uploadingEllipsis else if (photoUrl == null) AdminT.addPhoto else AdminT.replacePhoto,
                enabled = !uploading,
            ) { file ->
                uploading = true
                scope.launch {
                    val url = runCatching { repository.uploadMedia(rid, "item", file) }.getOrNull()
                    uploading = false
                    if (url != null) photoUrl = url else error = AdminT.photoUploadFailed
                }
            }
        }

        AdminError(error)

        AdminButton(
            text = if (existing == null) AdminT.addDish else AdminT.save,
            enabled = formLangs.any { names[it].orEmpty().isNotBlank() } &&
                priceUah.toDoubleOrNull() != null,
            modifier = Modifier.fillMaxWidth().margin(top = 14.px),
            onClick = {
                val priceMinor = ((priceUah.toDoubleOrNull() ?: 0.0) * 100).toInt()
                val name = names.mapValues { it.value.trim() }.filterValues { it.isNotBlank() }
                val description = descriptions.mapValues { it.value.trim() }.filterValues { it.isNotBlank() }
                scope.launch {
                    val result =
                        runCatching {
                            if (existing == null) {
                                repository.createItem(
                                    rid,
                                    CreateItemRequestDto(
                                        categoryId = categoryId,
                                        name = name,
                                        description = description,
                                        priceMinor = priceMinor,
                                        photoUrl = photoUrl,
                                        weight = weight.takeIf { it.isNotBlank() },
                                        tags = tags.toList(),
                                        allergens = allergens.toList(),
                                        calories = calories.toIntOrNull(),
                                        isFeatured = featured,
                                    ),
                                ).data
                            } else {
                                repository.updateItem(
                                    rid,
                                    existing.id,
                                    UpdateItemRequestDto(
                                        categoryId = categoryId,
                                        name = name,
                                        description = description.takeIf { it.isNotEmpty() },
                                        priceMinor = priceMinor,
                                        photoUrl = photoUrl,
                                        weight = weight.takeIf { it.isNotBlank() },
                                        tags = tags.toList(),
                                        allergens = allergens.toList(),
                                        calories = calories.toIntOrNull(),
                                        isFeatured = featured,
                                    ),
                                )
                            }
                        }.getOrNull()
                    if (result != null) {
                        toast.success(if (existing == null) AdminT.dishAdded else AdminT.savedToast)
                        onDone()
                    } else {
                        error = AdminT.saveFailed
                    }
                }
            },
        )
    }
}

@Composable
private fun ChipPicker(
    label: String,
    options: List<Pair<String, String>>,
    selected: Set<String>,
    onChange: (Set<String>) -> Unit,
) {
    AdminHint(label)
    Row(Modifier.margin(bottom = 4.px)) {
        options.forEach { (value, title) ->
            val isSelected = value in selected
            Box(
                Modifier
                    .margin(right = 6.px)
                    .backgroundColor(if (isSelected) Brand.Emerald else Brand.WarmBackground)
                    .borderRadius(999.px)
                    .padding(topBottom = 4.px, leftRight = 10.px)
                    .cursor(Cursor.Pointer)
                    .onClick {
                        onChange(if (isSelected) selected - value else selected + value)
                    },
            ) {
                SpanText(
                    title,
                    Modifier.fontSize(12.px)
                        .color(if (isSelected) Brand.Surface else Brand.TextSecondary),
                )
            }
        }
    }
}

/** Centered popup shell: dimmed backdrop, titled header, close button and body. */
@Composable
private fun MenuModal(
    title: String,
    iconSvg: String,
    onClose: () -> Unit,
    body: @Composable () -> Unit,
) {
    Div({
        classes("qk-menu-overlay")
        onClick { onClose() }
    }) {
        Div({
            classes("qk-menu-modal")
            // Clicks inside the card must not bubble up to the backdrop.
            onClick { it.stopPropagation() }
        }) {
            Div({ classes("qk-menu-modal-head") }) {
                Div({ classes("t") }) {
                    Div({ classes("i") }) { InlineIcon(iconSvg, 18, "#FFFFFF") }
                    Span { Text(title) }
                }
                org.jetbrains.compose.web.dom.Button({
                    classes("qk-menu-modal-x")
                    attr("aria-label", AdminT.closeLabel)
                    onClick { onClose() }
                }) { InlineIcon(MenuIcons.Close, 16, "#6B7280") }
            }
            Div({ classes("qk-menu-modal-body") }) { body() }
        }
    }
}

/** Create / edit a category (localized names) inside a popup. */
@Composable
private fun CategoryModal(
    repository: AdminRepository,
    scope: CoroutineScope,
    rid: String,
    languages: List<String>,
    existing: AdminMenuCategoryBlockDto?,
    onClose: () -> Unit,
    onSaved: () -> Unit,
) {
    var names by remember { mutableStateOf(existing?.category?.name.orEmpty()) }
    // Bar categories route their dishes (drinks) to the bartender's board instead of the cook's.
    var isBar by remember { mutableStateOf(existing?.category?.isBar ?: false) }
    val toast = com.qrkontaq.ui.components.notifications.LocalToastState.current
    val formLangs = orderedLangs(languages)

    MenuModal(
        title = if (existing == null) AdminT.addCategory else AdminT.editCategory,
        iconSvg = if (existing == null) AdminIcons.Plus else AdminIcons.Pencil,
        onClose = onClose,
    ) {
        Row(Modifier.fillMaxWidth().flexWrap(org.jetbrains.compose.web.css.FlexWrap.Wrap).gap(8.px)) {
            formLangs.forEach { code ->
                Box(Modifier.styleModifier { property("flex", "1"); property("min-width", "170px") }) {
                    TextInput(
                        value = names[code].orEmpty(),
                        onValueChange = { names = names + (code to it) },
                        label = "${AdminT.nameLabel} (${code.uppercase()})",
                    )
                }
            }
        }
        Row(Modifier.margin(topBottom = 10.px), verticalAlignment = Alignment.CenterVertically) {
            AdminToggle(checked = isBar) { isBar = it }
            SpanText(
                AdminT.barCategory,
                Modifier.fontSize(13.px).color(Brand.Charcoal).margin(left = 8.px),
            )
        }
        AdminButton(
            text = if (existing == null) AdminT.addCategory else AdminT.save,
            iconSvg = if (existing == null) AdminIcons.Plus else null,
            enabled = formLangs.any { names[it].orEmpty().isNotBlank() },
            modifier = Modifier.fillMaxWidth().margin(top = 14.px),
            onClick = {
                val name = names.mapValues { it.value.trim() }.filterValues { it.isNotBlank() }
                scope.launch {
                    val ok =
                        runCatching {
                            if (existing == null) {
                                repository.createCategory(rid, name, isBar)
                            } else {
                                repository.updateCategory(
                                    rid,
                                    existing.category.id,
                                    UpdateCategoryRequestDto(name = name, isBar = isBar),
                                )
                            }
                        }.isSuccess
                    if (ok) {
                        toast.success(if (existing == null) AdminT.dishAdded else AdminT.savedToast)
                        onSaved()
                    } else {
                        toast.error(AdminT.saveFailed)
                    }
                }
            },
        )
    }
}

/** Create / edit a dish inside a popup (wraps the shared [ItemForm]). */
@Composable
private fun DishModal(
    repository: AdminRepository,
    scope: CoroutineScope,
    rid: String,
    languages: List<String>,
    currency: String,
    categories: List<AdminMenuCategoryBlockDto>,
    existing: AdminMenuItemDto?,
    initialCategoryId: String?,
    onClose: () -> Unit,
    onSaved: () -> Unit,
) {
    MenuModal(
        title = if (existing == null) AdminT.addDish else AdminT.editDish,
        iconSvg = if (existing == null) AdminIcons.Plus else AdminIcons.Pencil,
        onClose = onClose,
    ) {
        ItemForm(
            repository = repository,
            scope = scope,
            rid = rid,
            languages = languages,
            currency = currency,
            categories = categories,
            initialCategoryId = initialCategoryId,
            existing = existing,
            onDone = onSaved,
        )
    }
}
